import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import Paper from '@material-ui/core/Paper';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Done from '@material-ui/icons/Done';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { uiState } from 'react-redux-ui-state';
import jwt from 'jsonwebtoken';

import BusyButton from 'components/BusyButton/BusyButton';
import ORTypography from 'components/ORTypography/ORTypography';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import { required, maxLength } from 'services/validationMsgs';
import { lightTheme } from '../App/App.theme';

const propTypes = {
  inviteTokenValidatedData: PropTypes.shape({
    newJwt: PropTypes.string,
    admin: PropTypes.string,
    invite: PropTypes.string,
    role: PropTypes.string,
    companyName: PropTypes.string,
  }).isRequired,
  acceptInvitation: PropTypes.func.isRequired,
  acceptInvitationRedirect: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  passwordLock: PropTypes.bool.isRequired,
  hasEight: PropTypes.bool.isRequired,
  hasLowercase: PropTypes.bool.isRequired,
  hasUppercase: PropTypes.bool.isRequired,
  hasNumsSpecialChars: PropTypes.bool.isRequired,
  terms: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  classes: PropTypes.shape({}).isRequired,
};

const handleFormSubmissionReturn = (result) => {
  if (result.error) {
    throw new SubmissionError(result.formErrors);
  }
};

const maxLength128 = maxLength(128);

export function UnstyledUserInvitationSignupPage({
  inviteTokenValidatedData,
  acceptInvitation,
  acceptInvitationRedirect,
  submitting,
  valid,
  passwordLock,
  hasEight,
  hasLowercase,
  hasUppercase,
  hasNumsSpecialChars,
  terms,
  setUiState,
  handleSubmit,
  classes,
}) {
  const handleCheckBoxChange = () => {
    setUiState({
      terms: !terms,
    });
  };

  const checkLowercase = new RegExp(/[a-z]/);
  const checkUppercase = new RegExp(/[A-Z]/);
  const checkNumbers = new RegExp(/[0-9]/);
  const checkSpecialChars = new RegExp(/[!@#$%^&*(),.?":{}|<>]/);

  const checkPasswordValidity = (event) => {
    const string = event.currentTarget.value;

    setUiState({
      hasEight: string.length >= 8,
      hasUppercase: checkUppercase.test(string),
      hasLowercase: checkLowercase.test(string),
      hasNumsSpecialChars: checkNumbers.test(string) || checkSpecialChars.test(string),
    });

    if (
      string.length >= 8 &&
      checkUppercase.test(string) &&
      checkLowercase.test(string) &&
      (checkNumbers.test(string) || checkSpecialChars.test(string))
    ) {
      setUiState({
        passwordLock: false,
      });
    } else {
      setUiState({
        passwordLock: true,
      });
    }
  };

  const { newJwt, admin, invite, role, companyName } = inviteTokenValidatedData;

  const decodedToken = jwt.decode(newJwt);
  const needsSignup = !decodedToken.isExistingUserWithoutOrg;

  const submitHandler = (data) =>
    acceptInvitation(newJwt, data, needsSignup)
      .then(handleFormSubmissionReturn)
      .then(acceptInvitationRedirect);

  return (
    <div className={classes.container}>
      <div className={classes.contentContainer}>
        <div className={classes.logoContainer}>
          <img
            src="/images/logos/or-logo-white-no-tagline.svg"
            className={classes.logo}
            alt="Object Rocket"
          />
        </div>
        <div className={classes.textBlobContainer}>
          <ORTypography align="center" variant="h3" className={classes.acceptText}>
            Invitation Accepted!
          </ORTypography>
          <ORTypography variant="h4" className={classes.verifyText}>
            Verify Your Information
          </ORTypography>
          <ORTypography align="center" className={classes.inviteText}>
            {admin}
            &nbsp;has invited you to join&nbsp;
            <br />
            {companyName}
            &nbsp;as a&nbsp;
            {role}
            .
            <br />
            <em>You will be redirected to a login screen.</em>
          </ORTypography>
        </div>

        <ThemeProvider theme={lightTheme}>
          <form className={classes.formContainer} onSubmit={handleSubmit(submitHandler)}>
            <Paper>
              {needsSignup && (
                <div className={classes.fieldContainer}>
                  <Field
                    name="email"
                    placeholder={invite}
                    component={WrappedTextField}
                    helperText="Don't worry, we won't spam your inbox."
                    fullWidth
                    variant="outlined"
                    disabled
                    className={classes.field}
                  />
                  <Field
                    name="firstname"
                    placeholder="First Name"
                    component={WrappedTextField}
                    validate={[required]}
                    fullWidth
                    variant="outlined"
                    className={classes.field}
                  />
                  <Field
                    name="lastname"
                    placeholder="Last Name"
                    component={WrappedTextField}
                    validate={[required]}
                    fullWidth
                    variant="outlined"
                    className={classes.field}
                  />
                  <Field
                    variant="outlined"
                    name="password"
                    placeholder="Password"
                    type="password"
                    component={WrappedTextField}
                    validate={[maxLength128, required]}
                    onChange={checkPasswordValidity}
                    props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
                    fullWidth
                    className={classes.field}
                  />
                  <ORTypography variant="caption">
                    Passwords need to contain:
                    <br />
                    &nbsp;&nbsp;-&nbsp;at least 8 characters
                    {hasEight && (
                      <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                    )}
                    <br />
                    &nbsp;&nbsp;-&nbsp;uppercase letters
                    {hasUppercase && (
                      <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                    )}
                    <br />
                    &nbsp;&nbsp;-&nbsp;lowercase letters
                    {hasLowercase && (
                      <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                    )}
                    <br />
                    &nbsp;&nbsp;-&nbsp;numbers or special characters
                    {hasNumsSpecialChars && (
                      <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                    )}
                  </ORTypography>
                </div>
              )}
              <div className={classes.termsCheckbox}>
                <FormControlLabel
                  classes={{ root: classes.termsCheckboxControl }}
                  control={<Checkbox />}
                  onChange={handleCheckBoxChange}
                  checked={terms}
                  label="I agree to the Terms and Conditions"
                />
              </div>
            </Paper>
            <div className={classes.buttonContainer}>
              <BusyButton
                size="large"
                name="submit"
                type="submit"
                fullWidth
                loading={submitting}
                disabled={needsSignup ? !terms || !valid || passwordLock || !invite : !terms}
                variant="contained"
                color="primary"
                className={classes.acceptInviteButton}
              >
                <span className={classes.buttonText}>Confirm</span>
              </BusyButton>
            </div>
          </form>
        </ThemeProvider>
      </div>
    </div>
  );
}

UnstyledUserInvitationSignupPage.propTypes = propTypes;

const styles = (theme) => ({
  acceptInviteButton: {
    backgroundColor: theme.customColors.orange,
  },
  checkmarkRoot: {
    color: theme.palette.secondary.main,
    verticalAlign: 'top',
  },
  container: {
    overflow: 'hidden',
    background: 'radial-gradient(gray, #000)',
    backgroundSize: 'cover',
    height: '100%',
  },
  contentContainer: {
    position: 'absolute',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  logoContainer: {
    margin: theme.spacing(2),
  },
  textBlobContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'space-around',
    margin: theme.spacing(3),
  },
  acceptText: {
    margin: theme.spacing(1),
    fontWeight: '500',
  },
  verifyText: {
    margin: theme.spacing(1),
    fontWeight: '500',
  },
  inviteText: {
    margin: theme.spacing(1),
  },
  formContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
    margin: theme.spacing(1),
    maxWidth: theme.spacing(75),
  },
  fieldContainer: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
  field: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  termsCheckbox: {
    margin: theme.spacing(2),
  },
  termsCheckboxControl: {
    '& span': {
      fontSize: theme.spacing(1.75),
      fontWeight: '400',
      fontStyle: 'italic',
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
});

export const uiConfig = {
  name: 'userInviteSignup',
  state: () => ({
    passwordLock: true,
    terms: false,
    hasEight: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumsSpecialChars: false,
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(reduxForm({ form: 'userInvite' })(UnstyledUserInvitationSignupPage)),
);
